import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { ServicesState } from "@reducers/types/HRM/services-state";

import {
  getServicesRequest,
  updateOtherServicesInfoRequest,
  updateServicesHistoryRequest,
  updatePromotionParticularsInfoRequest,
  deleteOtherServicesInfoRequest,
  deleteServicesHistoryInfoRequest,
  deletePromotionParticularsInfoRequest,
} from "@actions/HRM/employee/services-actions";
import { getServicesState } from "@selectors/HRM/employee/services-selector";

import {
  OtherServiceType,
  PromotionParticularType,
  ServicesHistoryType,
  ServicesType,
} from "@type/HRM/services";
import { useAppSelector } from "@helpers/redux";

interface UseServices {
  getServicesRequestOngoing: boolean;

  updateServiceHistoryRequestOngoing: boolean;
  updateServiceHistorySuccess: boolean;
  updateServiceHistoryFailed: boolean;

  updatePromotionParticularRequestOngoing: boolean;
  updatePromotionParticularSuccess: boolean;
  updatePromotionParticularFailed: boolean;

  updateOtherServiceRequestOngoing: boolean;
  updateOtherServiceSuccess: boolean;
  updateOtherServiceFailed: boolean;

  deleteServicesHistoryRequestOngoing: boolean;
  deleteServicesHistorySuccess: boolean;
  deleteServicesHistoryFailed: boolean;

  deletePromotionParticularRequestOngoing: boolean;
  deletePromotionParticularSuccess: boolean;
  deletePromotionParticularFailed: boolean;

  deleteOtherServiceRequestOngoing: boolean;
  deleteOtherServiceSuccess: boolean;
  deleteOtherServiceFailed: boolean;

  servicesInfo: ServicesType;

  changeServicesHistoryInfo: (
    updateServicesHistoryInfo: ServicesHistoryType,
  ) => void;
  changePromotionParticularsInfo: (
    updatePromotionParticularsInfo: PromotionParticularType,
  ) => void;
  changeOtherServicesInfo: (updateOtherServicesInfo: OtherServiceType) => void;

  removeServicesHistoryInfo: (servicesHistoryID: number) => void;
  removePromotionParticularsInfo: (PromotionParticularID: number) => void;
  removeOtherServicesInfo: (otherServiceID: number) => void;
}

export const useServices = (id: number | null): UseServices => {
  const dispatch = useDispatch();
  const {
    data,

    getServicesRequestOngoing,

    updateServiceHistoryRequestOngoing,
    updateServiceHistorySuccess,
    updateServiceHistoryFailed,

    updatePromotionParticularRequestOngoing,
    updatePromotionParticularSuccess,
    updatePromotionParticularFailed,

    updateOtherServiceRequestOngoing,
    updateOtherServiceSuccess,
    updateOtherServiceFailed,

    deleteServicesHistoryRequestOngoing,
    deleteServicesHistorySuccess,
    deleteServicesHistoryFailed,

    deletePromotionParticularRequestOngoing,
    deletePromotionParticularSuccess,
    deletePromotionParticularFailed,

    deleteOtherServiceRequestOngoing,
    deleteOtherServiceSuccess,
    deleteOtherServiceFailed,
  } = useAppSelector<ServicesState>(getServicesState);

  //GET
  useEffect(() => {
    dispatch(getServicesRequest(id));
  }, [dispatch, id]);
  const services = data;

  //UPDATE SERVICE HISTORY INFO
  const changeServicesHistoryInfo = (
    updateServicesHistoryInfo: ServicesHistoryType,
  ) =>
    dispatch(
      updateServicesHistoryRequest({
        employeeID: id,
        ...updateServicesHistoryInfo,
      } as ServicesHistoryType),
    );

  //UPDATE PROMOTION PARTICULARS INFO
  const changePromotionParticularsInfo = (
    updatePromotionParticularsInfo: PromotionParticularType,
  ) =>
    dispatch(
      updatePromotionParticularsInfoRequest({
        employeeID: id,
        ...updatePromotionParticularsInfo,
      } as PromotionParticularType),
    );

  //UPDATE OTHER SERVICES INFO
  const changeOtherServicesInfo = (updateOtherServicesInfo: OtherServiceType) =>
    dispatch(
      updateOtherServicesInfoRequest({
        employeeID: id,
        ...updateOtherServicesInfo,
      } as OtherServiceType),
    );

  //DELETE SERVICE HISTORY INFO
  const removeServicesHistoryInfo = (servicesHistoryID: number) =>
    dispatch(
      deleteServicesHistoryInfoRequest({
        employeeID: id,
        servicesHistoryID,
      }),
    );

  //DELETE PROMOTION PARTICULARS INFO
  const removePromotionParticularsInfo = (promotionParticularID: number) =>
    dispatch(
      deletePromotionParticularsInfoRequest({
        employeeID: id,
        promotionParticularID,
      }),
    );

  //DELETE OTHER SERVICES INFO
  const removeOtherServicesInfo = (otherServiceID: number) =>
    dispatch(
      deleteOtherServicesInfoRequest({
        employeeID: id,
        otherServiceID,
      }),
    );

  return {
    getServicesRequestOngoing,

    updateServiceHistoryRequestOngoing,
    updateServiceHistorySuccess,
    updateServiceHistoryFailed,

    updatePromotionParticularRequestOngoing,
    updatePromotionParticularSuccess,
    updatePromotionParticularFailed,

    updateOtherServiceRequestOngoing,
    updateOtherServiceSuccess,
    updateOtherServiceFailed,

    deleteServicesHistoryRequestOngoing,
    deleteServicesHistorySuccess,
    deleteServicesHistoryFailed,

    deletePromotionParticularRequestOngoing,
    deletePromotionParticularSuccess,
    deletePromotionParticularFailed,

    deleteOtherServiceRequestOngoing,
    deleteOtherServiceSuccess,
    deleteOtherServiceFailed,

    servicesInfo: services as ServicesType,

    changeOtherServicesInfo,
    changePromotionParticularsInfo,
    changeServicesHistoryInfo,

    removeOtherServicesInfo,
    removePromotionParticularsInfo,
    removeServicesHistoryInfo,
  };
};
